import * as React from 'react'
import { I18n } from "react-redux-i18n";
import { Modal, ModalBody, ModalFooter, ModalHeader, ModalTitle } from '@common/components/modal';
import pick from "lodash/pick";
import cx from 'classnames';
import isNil from 'lodash/isNil';

import { getTestAttributes } from "@common/utils/tests";
import { Flex } from "@common/components/Flex";
import { FolderGetDto } from '@common/dto/folder/get-dto';
import { FolderListDto } from '@common/dto/folder/list-dto';
import { FolderFormatsDto, FolderSettingDto, FolderSettingUpdateDto, FolderTemplateFormatId, FolderTemplateFormatName, FolderTemplateOption, FormatFoldersDto, SelectedFolderSecuritySetting } from '@common/dto/folder-settings';
import { HttpErrorDto } from '@common/dto/http';
import { CreateFormatFolderParams, FolderFormatConfig } from '@common/actions/formats';
import { Button } from '@common/components/Button';

import { Settings } from './components/settings';
import { Folders } from './components/folders';
import { TemplateOptions } from './components/template-options';
import { Loading } from '../loading';
import { Formats } from './components/formats';
import { defaultFormatIdKeys, defaultFormatNameKeys, folderTemplateOptionKeys } from './folder-management.consts';
import { MessageProps } from '../MessageBox.types';

import './folder-management.styles.scss';

export enum FolderManagementActionMode {
  create = 'create',
  edit = 'edit',
};

type FolderManagementProps = {
  mode?: FolderManagementActionMode;
  onClose: () => void;
  folder?: FolderGetDto;
  errors?: string[];
  folders: FolderListDto[];
  showErrorMessage: (message?: string | undefined, size?: MessageProps['size']) => void;
  getFolderSecuritySettingsClasses: () => Promise<FolderSettingDto[]>;
  getFolder: (folderId: FolderSettingDto['id']) => Promise<FolderGetDto>;
  onSave: (folderDto: Partial<FolderSettingUpdateDto>) => Promise<void>;
  getFormatFolders?: (type: keyof FolderTemplateFormatId) => Promise<FormatFoldersDto[] | undefined>;
  getFolderFormats?: (type: keyof FolderTemplateFormatId, queries?: FolderFormatConfig['queries']) => Promise<FolderFormatsDto[] | undefined>;
  onCreateFormatFolder?: (type: keyof FolderTemplateFormatId, data: CreateFormatFolderParams) => Promise<void>;
  onDeleteFormatFolder?: (type: keyof FolderTemplateFormatId, queries: FolderFormatConfig['queries']) => Promise<void>;
  onDeleteFormat?: (type: keyof FolderTemplateFormatId, queries: FolderFormatConfig['queries']) => Promise<void>;
  showTemplateOptions?: boolean;
  classes?: {
    folderManagementFolders?: {
      folders?: string;
      foldersTree?: string;
    }
  }
};

type LoadedDtoKeys = {
  folderSecuritySettings?: boolean;
  selectedFolder?: boolean;
};

export const FolderManagement = ({
  mode,
  folders,
  folder,
  showTemplateOptions = true,
  classes,
  onClose,
  onSave,
  showErrorMessage,
  getFolderSecuritySettingsClasses,
  getFolder,
  getFormatFolders,
  getFolderFormats,
  onCreateFormatFolder,
  onDeleteFormatFolder,
  onDeleteFormat,
}: FolderManagementProps) => {

  const isEditMode = mode === FolderManagementActionMode.edit;

  const [submitting, setSubmitting] = React.useState(false);
  const [isOpenFormatModal, setIsOpenFormatModal] = React.useState(false);
  const [loading, setLoading] = React.useState<LoadedDtoKeys>({});
  const [folderSecuritySettings, setFolderSecuritySettings] = React.useState<Record<FolderSettingDto['id'], FolderSettingDto>>();

  const [overrideSec, setOverrideSec] = React.useState(false);
  const [folderName, setFolderName] = React.useState('');
  const [selectedFolder, setSelectedFolder] = React.useState<FolderGetDto | undefined>(folder);
  const [selectedFolderSecuritySetting, setSelectedFolderSecuritySetting] = React.useState<Record<FolderSettingDto['secIndex'], SelectedFolderSecuritySetting>>();

  const [templateOptions, setTemplateOptions] = React.useState<Partial<FolderTemplateOption>>({});
  const [defaultFormatIds, setDefaultFormatIds] = React.useState<Partial<FolderTemplateFormatId>>({});
  const [defaultFormatNames, setDefaultFormatNames] = React.useState<Partial<FolderTemplateFormatName>>({});

  const disableActions = Object.values(loading).some(isLoading => isLoading) || submitting;
  const disableSubmitAction = isEditMode ? disableActions : (disableActions || !folderName.trim().length);

  const handleOpenFormatModal = React.useCallback(() => {
    setIsOpenFormatModal(true);
  }, [setIsOpenFormatModal]);

  const handleCloseFormatModal = React.useCallback(() => {
    setIsOpenFormatModal(false);
  }, [setIsOpenFormatModal]);

  const handleFormatChange = React.useCallback((data: { defaultFormatIds: Partial<FolderTemplateFormatId>, defaultFormatNames: Partial<FolderTemplateFormatName> }) => {
    const { defaultFormatIds, defaultFormatNames } = data;
    setDefaultFormatIds(prev => ({
      ...prev,
      ...defaultFormatIds
    }));
    setDefaultFormatNames(prev => ({
      ...prev,
      ...defaultFormatNames
    }))
  }, [setDefaultFormatIds, setDefaultFormatNames]);

  const getDefaultSelectedFolderSecuritySetting = React.useCallback((): Required<SelectedFolderSecuritySetting> => {
    return { secIndex: 1, readFoldSec: 0, addSec: 0, delSec: 0, modSec: 0, readSec: 0, writeSec: 0 };
  }, []);

  const getFolderSecuritySettings = async () => {
    try {
      setLoading((prev) => ({ ...prev, folderSecuritySettings: true }));
      const settings = await getFolderSecuritySettingsClasses();
  
      const processedSettings = settings.reduce((acc: Record<FolderSettingDto['id'], FolderSettingDto>, item) => {
        acc[item.id] = item;
        return acc;
      }, {});
  
      setFolderSecuritySettings(processedSettings);
      return settings;
    } catch (err) {
      console.error(err);

      const errorMessage = (err as HttpErrorDto)?.entity || I18n.t('Failed to get Folder Security Settings');
      showErrorMessage(errorMessage); 
    } finally {
      setLoading((prev) => ({ ...prev, folderSecuritySettings: false }));
    }
  };

  const [fetchFolderOnce, setFetchFolderOnce] = React.useState(false);

  const getSelectedFolder = async (folderId: FolderListDto['id']) => {
    if (folder && !fetchFolderOnce) {
      try {
        setLoading((prev) => ({ ...prev, selectedFolder: true }));
        const folderData = await getFolder(folderId);
        setLoading((prev) => ({ ...prev, selectedFolder: false }));
        setSelectedFolder(folderData);
        setFetchFolderOnce(true);
  
        const defaultselectedSettings = folderData?.security?.reduce((acc: Record<FolderSettingDto['secIndex'], SelectedFolderSecuritySetting>, item) => {
          acc[item.secIndex] = item;
          return acc;
        }, {});
        setSelectedFolderSecuritySetting(defaultselectedSettings);
        setOverrideSec(folderData.overrideSec);
  
        setDefaultFormatIds(pick(folderData, defaultFormatIdKeys) as FolderTemplateFormatId);
        setDefaultFormatNames(pick(folderData, defaultFormatNameKeys) as FolderTemplateFormatName);
        setTemplateOptions(pick(folderData, folderTemplateOptionKeys) as FolderTemplateOption);
      } catch (err) {
        console.error(err);

        const errorMessage = (err as HttpErrorDto)?.entity || I18n.t('Failed to get selected folder');
        showErrorMessage(errorMessage);
      }
    }
  };

  const handleSubmit = async () => {
    try {
      setSubmitting(true);
      const security = selectedFolderSecuritySetting ? Object.values(selectedFolderSecuritySetting) : [getDefaultSelectedFolderSecuritySetting()];

      const cleanedFormatIds = { ...defaultFormatIds };
      const cleanedFormatNames = { ...defaultFormatNames };
      
      if (!templateOptions.tempIds) {
        cleanedFormatIds.defaultIdsFormatId = null;
        cleanedFormatNames.defaultIdsFormatName = null;
      }
      if (!templateOptions.tempPds) {
        cleanedFormatIds.defaultPdsFormatId = null;
        cleanedFormatNames.defaultPdsFormatName = null;
      }
      
      await onSave({
        overrideSec,
        security,
        ...(isEditMode ? {
          id: selectedFolder!.id,
          parentId: selectedFolder!.parentId,
          name: selectedFolder!.name,
        } : {
          parentId: selectedFolder?.id,
          name: folderName,
          id: null
        }),
        ...templateOptions,
        ...cleanedFormatIds,
        ...cleanedFormatNames,
      });
    } catch (err) {
      console.error(err);
      setSubmitting(false);
      const errorMessage = (err as HttpErrorDto)?.entity || I18n.t('Failed to save Folder');
      showErrorMessage(errorMessage);
    }
  };

  React.useEffect(() => {
    getFolderSecuritySettings();
  }, []);

  const getFolders = React.useCallback(async (type: keyof FolderTemplateFormatId): Promise<FormatFoldersDto[] | undefined> => {
    if (getFormatFolders) {
      try {
        const formatFolders = await getFormatFolders(type);
  
        return formatFolders;
      } catch (err) {
        console.error(err);
  
        const errorMessage = (err as HttpErrorDto)?.entity || I18n.t('Failed to get Format folders');
        showErrorMessage(errorMessage);
      }
    }
  }, [showErrorMessage, getFormatFolders]);

  const getFormats = React.useCallback(async (type: keyof FolderTemplateFormatId, queries?: FolderFormatConfig['queries']): Promise<FolderFormatsDto[] | undefined> => {
   if (getFolderFormats) {
    try {
      const folderFormats = await getFolderFormats(type, queries);

      return folderFormats;
    } catch (err) {
      console.error(err);

      const errorMessage = (err as HttpErrorDto)?.entity || I18n.t('Failed to get Folder formats');
      showErrorMessage(errorMessage);
    }
   }
  }, [showErrorMessage, getFolderFormats]);

  const createFormatFolder = React.useCallback(async (type: keyof FolderTemplateFormatId, data: CreateFormatFolderParams): Promise<void> => {
    if (onCreateFormatFolder) {
      try {
        await onCreateFormatFolder(type, data);
      } catch (err) {
        console.error(err);
  
        const errorMessage = (err as HttpErrorDto)?.entity || I18n.t('Failed to get create formats folder');
        showErrorMessage(errorMessage);
      }
    }
  }, [showErrorMessage, onCreateFormatFolder]);

  const deleteFormatFolder = React.useCallback(async (type: keyof FolderTemplateFormatId, folderId: number): Promise<void> => {
    if (onDeleteFormatFolder) {
      try {
        await onDeleteFormatFolder(type, { folderId });
      } catch (err) {
        console.error(err);
  
        const errorMessage = (err as HttpErrorDto)?.entity || I18n.t('Failed to get delete formats folder');
        showErrorMessage(errorMessage);
      }
    }
  }, [showErrorMessage, onCreateFormatFolder]);

  const deleteFormat = React.useCallback(async (type: keyof FolderTemplateFormatId, formatId: number): Promise<void> => {
    if (onDeleteFormat) {
      try {
        await onDeleteFormat(type, { formatId });
      } catch (err) {
        console.error(err);
  
        const errorMessage = (err as HttpErrorDto)?.entity || I18n.t('Failed to get delete formats');
        showErrorMessage(errorMessage);
      }
    }
  }, [showErrorMessage, onCreateFormatFolder]);

  React.useEffect(() => {
    if (!isNil(selectedFolder?.id)) {
      getSelectedFolder(selectedFolder!.id);
    } else {
      const defaulttemplateOptions: Array<keyof FolderTemplateOption> = ["tempIds", "tempPds", "tempSubtext", "tempSymbols"];
      const options = defaulttemplateOptions.reduce((acc, item) => {
        acc[item] = true;
        return acc;
      }, {} as FolderTemplateOption);

      setTemplateOptions(options);
    }
  }, [selectedFolder?.id]);


  return (
    <Modal
      show
      bsSize="medium"
      bsClass="FolderManagement bootstrap modal"
      onHide={onClose}
      onEscapeKeyDown={onClose}
      animation={false}
    >
      <ModalHeader closeButton onHide={onClose}>
        <ModalTitle
          {...getTestAttributes({ id: 'lbl-folder-settings-header' })}
        >
          {isEditMode ? I18n.t("Edit Folder Settings") : I18n.t("New Folder")}
        </ModalTitle>
      </ModalHeader>
      <ModalBody className={cx("FolderManagement__body", {
        ["FolderManagement__is-edit"]: isEditMode
      })}>
        {
          disableActions && (
            <Flex width="100%" justify="center" alignItems="center" className="FolderManagement__loading-content">
              <Loading size={25} />
            </Flex>
          )
        }
        <Flex>
          <Folders
            disabled={isEditMode}
            selectedFolder={selectedFolder}
            folders={folders}
            folderName={folderName}
            showFolderName={!isEditMode}
            onFolderNameChange={setFolderName}
            onSelectedFolderChange={setSelectedFolder}
            classes={classes?.folderManagementFolders}
            
          />
          <div>
            {
              showTemplateOptions && (
                <TemplateOptions
                  onChange={setTemplateOptions}
                  selectedTemplateOptions={templateOptions}
                  onOpenFormatModal={handleOpenFormatModal}
                />
              )
            }
            {isOpenFormatModal && (
              <Formats
                createFolder={createFormatFolder}
                deleteFolder={deleteFormatFolder}
                deleteFormat={deleteFormat}
                templateOptions={templateOptions}
                defaultFormatIds={defaultFormatIds}
                defaultFormatNames={defaultFormatNames}
                onCloseFormatModal={handleCloseFormatModal}
                getFormatFolders={getFolders}
                getFolderFormats={getFormats}
                onSubmit={handleFormatChange}
              />
            )}
          </div>
        </Flex>
        <Settings
          folderSecuritySettings={folderSecuritySettings}
          selectedFolderSecuritySetting={selectedFolderSecuritySetting}
          onSettingChange={setSelectedFolderSecuritySetting}
          onOverrideSecChange={setOverrideSec}
          overrideSec={overrideSec}
        />
      </ModalBody>
      <ModalFooter>
        <Flex justify="flex-end">
          <Button
            onClick={onClose}
            disabled={disableActions}
            {...getTestAttributes({ id: 'btn-folder-settings-cancel' })}
          >
            {I18n.t('Cancel')}
          </Button>
          <Button
            bsStyle="primary"
            onClick={handleSubmit}
            disabled={disableSubmitAction}
            {...getTestAttributes({ id: 'btn-folder-settings-save' })}
          >
            {isEditMode ? I18n.t("Save") : I18n.t("Create")}
          </Button>
        </Flex>
      </ModalFooter>
    </Modal>
  )

}